import { theme } from '@theme';
import { SuccessToast } from '@utils/ToastUtils';
import { FC, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { useOutsideClickDetection } from '../../../../../hooks';
import { usePortfolioBulkActionsContainer } from '../../../../../hooks/UsePortfolioBulkActionsContainer';
import { useWindowSize } from '../../../../../hooks/UseWindowSize';
import { FilterItemResource } from '../../../../../types/FilterTypes';
import { MailerStatusRequestResource } from '../../../../../types/PortfolioBulkActionResource';
import { PortfolioResource } from '../../../../../types/PortfolioResource';
import ButtonComponent from '../../../../shared/button/ButtonComponent';
import DropdownSelect from '../../../../shared/filters/dropdown-select/DropdownSelect';
import SingleSelectDropdownOptions from '../../../../shared/filters/single-select-dropdown/SingleSelectDropdownOptions';
import ConfirmationModal from '../../../../shared/modals/confirmation-modal/ConfirmationModal';
import { DropdownWrapper, OptionItem, OptionItemLabel, Typography } from '../../../../style';

type MailerStatusProps = {
  selectedRecords: Array<PortfolioResource>;
  selectedRecordsCount: number;
  isAllRowsSelected: boolean;
  isClearSelectionLink: boolean;
  totalRecordsCount: number;
  toggleSelectedRows: (value: boolean) => void;
};

const MailerStatus: FC<MailerStatusProps> = (props) => {
  const [isDatePickerVisible, setIsDatePickerVisible] = useState<boolean>(false);
  const [isPostMail, setIsPostMail] = useState<boolean>(false);
  const [dateValue, setDateValue] = useState<Date>(null);
  const [isMailerStatusModalOpen, setIsMailerStatusModalOpen] = useState<boolean>(false);
  const { createMailerStatusPayload, bulkUpdateMailerStatus } = usePortfolioBulkActionsContainer();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const windowHeight = useWindowSize()[1];
  const wrapperRef = useRef<HTMLElement>(null);
  useOutsideClickDetection(wrapperRef, setIsVisible);

  const options: FilterItemResource[] = [
    {
      key: 'PostMail',
      label: 'Do Not Mail Until...',
    },
    {
      key: 'Email',
      label: 'Do Not E-mail Until...',
    },
  ];

  const onSelectMailerStatus = (value: FilterItemResource) => {
    value.key === 'PostMail' ? setIsPostMail(true) : setIsPostMail(false);
    setIsDatePickerVisible(true);
  };

  const handleDateChange = (date: Date) => {
    setDateValue(date);
    setIsDatePickerVisible(false);
  };

  useEffect(() => {
    if (dateValue) {
      mailerStatusChanged();
    }
  }, [dateValue]);

  const mailerStatusChanged = () => {
    if (props.isAllRowsSelected && props.isClearSelectionLink) {
      setIsMailerStatusModalOpen(true);
    } else {
      mailerStatusChangedConfirmed();
    }
  };

  const mailerStatusChangedConfirmed = () => {
    let message: string = '';
    const count =
      props.isAllRowsSelected && props.isClearSelectionLink
        ? props.totalRecordsCount
        : props.selectedRecordsCount;

    if (props.selectedRecordsCount == 1) {
      message = 'Updated customer mailer status';
    } else {
      message = 'Updated mailer status of ' + count + ' customers';
    }

    let payload: MailerStatusRequestResource = createMailerStatusPayload(
      props.isAllRowsSelected,
      props.isClearSelectionLink,
      props.selectedRecords,
      dateValue,
      isPostMail,
    );
    const successCallback = () => {
      props.toggleSelectedRows(false);
      SuccessToast(message);
    };

    setIsMailerStatusModalOpen(false);

    bulkUpdateMailerStatus(payload, successCallback);
  };

  const handleClickOutSide = () => {
    setIsDatePickerVisible(false);
  };

  const handleCancel = (): void => {
    setIsMailerStatusModalOpen(false);
  };

  useEffect(() => {
    isDatePickerVisible && setIsVisible(true);
  }, [isVisible]);

  const generateOptions = (): JSX.Element[] => {
    return options?.map((item) => (
      <OptionItem width={100} key={item.key} onClick={() => onSelectMailerStatus(item)}>
        <OptionItemLabel>{item.label}</OptionItemLabel>
      </OptionItem>
    ));
  };

  const bodyComponent: JSX.Element = (
    <div className='mailer-status-modal-body'>
      <Typography
        size={theme.typography.h4}
        display='block'
        marginTop={15}
        fontWeight={theme.typography.fontWeights.semibold}
      >
        Before submitting, please ensure that all necessary changes have been made. Your predictions
        will be live within a few minutes of submission. You will receive an email when updates are
        complete.
      </Typography>
      <Typography size={theme.typography.h4} display='block' marginTop={10}>
        To be included in the predictive campaigns, please submit your changes by the communicated
        deadline.
      </Typography>
      <Typography size={theme.typography.h4} display='block' marginTop={10}>
        Contact your Dealer Relations Manager for more information.
      </Typography>
    </div>
  );

  return (
    <>
      <DropdownWrapper data-testid='mailer-status' ref={wrapperRef}>
        <div className='heap-filter-item'>
          <span
            data-testid='mailer-status-dropdown-header'
            onClick={() => setIsVisible(!isVisible)}
          >
            <DropdownSelect name={'Mailer Status'} />
          </span>
          {isVisible && (
            <SingleSelectDropdownOptions options={generateOptions()} filterHeight={36} />
          )}
        </div>
      </DropdownWrapper>

      {isDatePickerVisible && (
        <DatePickerWrapper
          data-testid='mailer-status-date-picker'
          className={
            windowHeight < 700
              ? 'dynamic-position'
              : isPostMail
                ? 'post-mail-status'
                : 'email-status'
          }
        >
          <DatePicker
            selected={new Date()}
            minDate={new Date()}
            onClickOutside={handleClickOutSide}
            onChange={handleDateChange}
            dropdownMode='select'
            inline
          >
            <DividerWrapper>
              <Divider>OR</Divider>
            </DividerWrapper>
            <ButtonComponent
              outerPadding='5px'
              innerPadding='5px 6px'
              color={theme.palette.white}
              backgroundColor={theme.palette.text.purple}
              text={'Stop Mailing'}
              fontWeight={theme.typography.fontWeights.semibold}
              fontSize={10}
              borderRadius={3}
              height={100}
              borderColor={theme.palette.transparent}
              onClick={() => handleDateChange(new Date('9999-12-31'))}
            />
          </DatePicker>
        </DatePickerWrapper>
      )}
      <ConfirmationModal
        isOpen={isMailerStatusModalOpen}
        handleSubmitClick={mailerStatusChangedConfirmed}
        handleCancelClick={handleCancel}
        cancelButtonText='Cancel'
        submitButtonText='Change Mailer Status'
        title='Confirm Change Mailer Status'
        bodyComponent={bodyComponent}
        showCloseIcon={false}
        padding={'20px 50px 20px 20px'}
        minHeight={'225px'}
      />
    </>
  );
};

export default MailerStatus;

const DatePickerWrapper = styled.div`
  transform: scale(1.5);
  margin-left: 17%;
  position: absolute;

  &.post-mail-status {
    margin-top: 41px;
  }

  &.email-status {
    margin-top: 74px;
  }

  &.dynamic-position {
    bottom: 45px;
  }

  .button-text {
    width: 100%;
  }

  .react-datepicker {
    font-family: 'proxima-nova-n4', 'proxima-nova', 'Helvetica', 'arial', sans-serif;
    font-size: 9px;
    display: flex;
    flex-direction: column;
    color: ${theme.palette.darkGray};
  }

  .react-datepicker__month-container {
    width: 155px;
    font-family: 'proxima-nova-n4', 'proxima-nova', 'Helvetica', 'arial', sans-serif;
    color: ${theme.palette.darkGray};
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header,
  .react-datepicker__day-name,
  .react-datepicker__time-name {
    color: ${theme.palette.darkGray};
  }

  .react-datepicker__day--disabled {
    color: ${theme.palette.lightGrey};
  }

  .react-datepicker__day--selected {
    color: ${theme.palette.text.white};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__year-text--selected,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--in-range,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background-color: ${theme.palette.purple};
  }
  .react-datepicker__day--outside-month {
    color: transparent;
    pointer-events: none;
  }

  .react-datepicker__day--selected:hover,
  .react-datepicker__day--in-selecting-range:hover,
  .react-datepicker__day--in-range:hover,
  .react-datepicker__month-text--selected:hover,
  .react-datepicker__month-text--in-selecting-range:hover,
  .react-datepicker__month-text--in-range:hover,
  .react-datepicker__quarter-text--selected:hover,
  .react-datepicker__quarter-text--in-selecting-range:hover,
  .react-datepicker__quarter-text--in-range:hover,
  .react-datepicker__year-text--selected:hover,
  .react-datepicker__year-text--in-selecting-range:hover,
  .react-datepicker__year-text--in-range:hover,
  .react-datepicker__day--keyboard-selected:hover,
  .react-datepicker__month-text--keyboard-selected:hover,
  .react-datepicker__quarter-text--keyboard-selected:hover,
  .react-datepicker__year-text--keyboard-selected:hover {
    background-color: ${theme.palette.purple};
  }

  .react-datepicker__children-container {
    width: auto;
  }
`;

const DividerWrapper = styled.div`
  text-align: center;
  margin: 5px 0 5px;
  background-color: ${theme.palette.background};
  height: 1px;
`;

const Divider = styled.span`
  color: ${theme.palette.darkGray};
  font-size: 8px;
  line-height: 0.5;
  background: ${theme.palette.white};
  padding: 0px 8px;
  top: -6px;
  position: relative;
  font-weight: ${theme.typography.fontWeights.semibold};
`;
